import React, { useState, useMemo, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { Toast } from './components/Toast';
import { Dashboard } from './components/views/Dashboard';
import { PatientRegistry } from './components/views/PatientRegistry';
import { AppointmentsView } from './components/views/Appointments';
import { CalendarView } from './components/views/CalendarView';
import { FollowUpView } from './components/views/FollowUp';
import { ReminderQueue } from './components/views/ReminderQueue';
import { Settings } from './components/views/Settings';
import { PatientModal } from './components/modals/PatientModal';
import { AppointmentModal } from './components/modals/AppointmentModal';
import { INITIAL_PATIENTS, INITIAL_DENTISTS, INITIAL_PROCEDURES, INITIAL_CLINIC_SETTINGS, INITIAL_APPOINTMENTS } from './constants/initialData';
import { Patient, Appointment, TabId, ToastState, ClinicSettings, Dentist, Procedure, ReminderItem, ReminderStatus } from './types';
import { checkAppointmentConflict, findAvailableSlots } from './utils/appointmentUtils';

const App: React.FC = () => {
  type UserSession =
    | { role: 'admin' }
    | { role: 'secretary' }
    | { role: 'doctor'; doctorId: string }
    | { role: 'patient'; patientId: string };

  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [patients, setPatients] = useState<Patient[]>(INITIAL_PATIENTS);
  const [appointments, setAppointments] = useState<Appointment[]>(INITIAL_APPOINTMENTS);
  const [dentists, setDentists] = useState<Dentist[]>(INITIAL_DENTISTS);
  const [procedures, setProcedures] = useState<Procedure[]>(INITIAL_PROCEDURES);
  const [clinicSettings, setClinicSettings] = useState<ClinicSettings>(INITIAL_CLINIC_SETTINGS);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(INITIAL_PATIENTS[0]?.id ?? null);
  const rooms = clinicSettings.treatmentRooms?.length ? clinicSettings.treatmentRooms : ['Chair 1'];
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);
  const [loginRole, setLoginRole] = useState<'admin' | 'secretary' | 'doctor' | 'patient'>('admin');
  const [loginDoctorId, setLoginDoctorId] = useState<string>(dentists[0]?.id ?? '');
  const [loginPatientId, setLoginPatientId] = useState<string>(patients[0]?.id ?? '');
  const [loginPin, setLoginPin] = useState<string>('');
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  useEffect(() => {
    if (loginRole === 'doctor') {
      const exists = dentists.find(d => d.id === loginDoctorId);
      if (!exists && dentists.length > 0) {
        setLoginDoctorId(dentists[0].id);
      }
    } else if (loginRole === 'patient') {
      const exists = patients.find(p => p.id === loginPatientId);
      if (!exists && patients.length > 0) {
        setLoginPatientId(patients[0].id);
      }
    }
  }, [loginRole, loginDoctorId, loginPatientId, dentists, patients]);

  useEffect(() => {
    if (currentUser?.role === 'doctor') {
      const exists = dentists.find(d => d.id === currentUser.doctorId);
      if (!exists) {
        setCurrentUser(null);
      }
    } else if (currentUser?.role === 'patient') {
      const exists = patients.find(p => p.id === currentUser.patientId);
      if (!exists) {
        setCurrentUser(null);
      }
    }
  }, [currentUser, dentists, patients]);
  const currentUserProfile = useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === 'doctor') {
      const doc = dentists.find(d => d.id === currentUser.doctorId);
      return {
        name: doc?.name || 'Doctor',
        title: doc?.specialty || 'Doctor',
        email: `${(doc?.name || 'doctor').toLowerCase().replace(/\s+/g, '.')}@dentalcore.com`,
      };
    }
    if (currentUser.role === 'patient') {
      const patient = patients.find(p => p.id === currentUser.patientId);
      return {
        name: patient?.name || 'Patient',
        title: 'Patient',
        email: patient?.email || `${(patient?.name || 'patient').toLowerCase().replace(/\s+/g, '.')}@email.com`,
      };
    }
    if (currentUser.role === 'secretary') {
      return { name: 'Front Desk', title: 'Secretary', email: 'frontdesk@dentalcore.com' };
    }
    return { name: 'Clinic Admin', title: 'Administrator', email: 'admin@dentalcore.com' };
  }, [currentUser, dentists, patients]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showToast, setShowToast] = useState<ToastState>({ show: false, message: '', type: 'success' });

  // Modals
  const [isApptModalOpen, setIsApptModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  // Helper to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Forms
  const getEmptyAppt = (): Omit<Appointment, 'id' | 'startTime'> => ({
    patientId: '',
    providerId: dentists[0]?.id || 'd1',
    date: getTodayDate(),
    time: '09:00',
    duration: 30,
    type: 'Cleaning',
    room: rooms[0],
    status: 'SCHEDULED',
    internalNotes: '',
    patientNotes: '',
    isWalkIn: false,
  });

  const getEmptyPatient = (): Omit<Patient, 'id'> => ({
    name: '',
    phone: '',
    email: '',
    dob: '',
    address: '',
    gender: 'Female',
    insurance: '',
    emergencyContact: { name: '', phone: '', relationship: '' },
    alerts: [],
    dentalHistory: '',
    attachments: [],
    auditLog: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const [apptForm, setApptForm] = useState<Omit<Appointment, 'id' | 'startTime'>>(getEmptyAppt());
  const [patientForm, setPatientForm] = useState<Omit<Patient, 'id'>>(getEmptyPatient());
  const [calendarFocusDate, setCalendarFocusDate] = useState<string | undefined>(() => getTodayDate());

  // Keep a patient selected when data changes
  useEffect(() => {
    if (patients.length === 0) {
      setSelectedPatientId(null);
      return;
    }

    const stillExists = selectedPatientId && patients.some(p => p.id === selectedPatientId);
    if (!stillExists) {
      setSelectedPatientId(patients[0].id);
    }
  }, [patients, selectedPatientId]);

  // Toast trigger
  const triggerToast = (message: string, type: 'success' | 'danger' = 'success') => {
    setShowToast({ show: true, message, type });
    setTimeout(() => setShowToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // SAVE APPOINTMENT
  const handleSaveAppt = (e: React.FormEvent) => {
    e.preventDefault();

    if (!apptForm.patientId) {
      alert('Please select a patient');
      return;
    }

    // Check for conflicts before saving
    const conflict = checkAppointmentConflict(apptForm, appointments, editingAppt?.id);
    if (conflict.hasConflict) {
      triggerToast(`Cannot book: ${conflict.message}`, 'danger');
      return;
    }

    const startStr = `${apptForm.date}T${apptForm.time}:00`;

    if (editingAppt) {
      // UPDATE existing appointment
      setAppointments(
        appointments.map(a =>
          a.id === editingAppt.id ? { ...apptForm, id: a.id, startTime: startStr } : a
        )
      );
      triggerToast('Appointment updated!');
      setCalendarFocusDate(apptForm.date);
    } else {
      // CREATE new appointment
      const newAppt: Appointment = {
        ...apptForm,
        id: `a${Date.now()}`,
        startTime: startStr,
      };
      setAppointments([...appointments, newAppt]);
      triggerToast('Appointment created!');
      setCalendarFocusDate(apptForm.date);
    }

    setIsApptModalOpen(false);
    setEditingAppt(null);
    setApptForm(getEmptyAppt());
  };

  // SAVE PATIENT
  const handleSavePatient = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingPatient) {
      // UPDATE existing patient
      setPatients(patients.map(p => (p.id === editingPatient.id ? { ...patientForm, id: p.id } : p)));
      triggerToast('Patient updated!');
    } else {
      // CREATE new patient
      const newPatient: Patient = {
        ...patientForm,
        id: `p${Date.now()}`,
      };
      setPatients([...patients, newPatient]);
      setSelectedPatientId(newPatient.id);
      triggerToast('Patient created!');
    }

    setIsPatientModalOpen(false);
    setEditingPatient(null);
    setPatientForm(getEmptyPatient());
  };

  const visibleAppointments = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'doctor') {
      return appointments.filter(a => a.providerId === currentUser.doctorId);
    }
    if (currentUser.role === 'patient') {
      return appointments.filter(a => a.patientId === currentUser.patientId);
    }
    // admin and secretary see all
    return appointments;
  }, [appointments, currentUser]);

  const visiblePatients = useMemo(() => {
    if (currentUser?.role === 'doctor') {
      const allowedIds = new Set(visibleAppointments.map(a => a.patientId));
      return patients.filter(p => allowedIds.has(p.id));
    }
    if (currentUser?.role === 'patient') {
      return patients.filter(p => p.id === currentUser.patientId);
    }
    return patients;
  }, [patients, currentUser, visibleAppointments]);

  const todayDateStr = new Date().toISOString().split('T')[0];

  const filteredPatients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return visiblePatients;

    return visiblePatients.filter(p => {
      const haystack = `${p.name} ${p.email} ${p.phone}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [visiblePatients, searchQuery]);

  // Keep selection within filtered set for faster “search to profile” navigation
  useEffect(() => {
    if (filteredPatients.length === 0) return;
    if (selectedPatientId && filteredPatients.some(p => p.id === selectedPatientId)) return;
    setSelectedPatientId(filteredPatients[0].id);
  }, [filteredPatients, selectedPatientId]);

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    setPatientForm(patient);
    setIsPatientModalOpen(true);
    setSelectedPatientId(patient.id);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    const updatedAppt =
      currentUser?.role === 'patient'
        ? { ...appointment, status: 'REQUESTED' as const }
        : appointment;
    setEditingAppt(appointment);
    setApptForm(updatedAppt);
    setIsApptModalOpen(true);
  };

  const handleDeleteAppointment = (id: string) => {
    setAppointments(prev => prev.filter(a => a.id !== id));
    triggerToast('Appointment deleted');
  };

  const enqueueReminder = (patientId: string, message?: string) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newReminder: ReminderItem = {
      id: `r${Date.now()}`,
      patientId,
      patientName: patient.name,
      contact: patient.email || patient.phone || 'N/A',
      status: 'queued',
      createdAt: new Date().toISOString(),
      message,
    };
    setReminders(prev => [newReminder, ...prev]);
    triggerToast('Reminder queued');
  };

  const updateReminderStatus = (id: string, status: ReminderStatus) => {
    setReminders(prev => prev.map(r => (r.id === id ? { ...r, status } : r)));
  };

  const deleteReminder = (id: string) => {
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const handleLogin = () => {
    if (loginRole === 'doctor' && !loginDoctorId) return;
    if (loginRole === 'patient' && !loginPatientId) return;
    if (loginRole === 'doctor') {
      setCurrentUser({ role: 'doctor', doctorId: loginDoctorId });
    } else if (loginRole === 'patient') {
      setCurrentUser({ role: 'patient', patientId: loginPatientId });
      setSelectedPatientId(loginPatientId);
    } else {
      setCurrentUser({ role: loginRole });
    }
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveTab('dashboard');
  };

  const handleCreateAppointment = (date: string, time: string) => {
    const providerId = currentUser?.role === 'doctor' ? currentUser.doctorId : dentists[0]?.id || 'd1';
    const room = rooms[0];
    const duration = 30;
    const patientId = currentUser?.role === 'patient' ? currentUser.patientId : '';

    const slots = findAvailableSlots(
      date,
      providerId,
      room,
      duration,
      appointments,
      8,
      18,
      clinicSettings.defaultSlotDuration || 30
    );

    const requested = time || '09:00';
    const requestedAppt: Omit<Appointment, 'id' | 'startTime'> = {
      ...getEmptyAppt(),
      providerId,
      room,
      duration: clinicSettings.defaultSlotDuration || duration,
      date,
      time: requested,
    };
    const conflict = checkAppointmentConflict(requestedAppt, appointments);
    const pickedTime = conflict.hasConflict && slots.length ? slots[0].time : requested;

    setEditingAppt(null);
    setApptForm({
      ...getEmptyAppt(),
      patientId,
      status: currentUser?.role === 'patient' ? 'REQUESTED' : 'SCHEDULED',
      providerId,
      room,
      duration: clinicSettings.defaultSlotDuration || duration,
      date,
      time: pickedTime,
    });
    setIsApptModalOpen(true);
  };

  const handleSaveSettings = (data: {
    settings: ClinicSettings;
    dentists: Dentist[];
    procedures: Procedure[];
    rooms: string[];
  }) => {
    const mergedSettings = { ...data.settings, treatmentRooms: data.rooms };
    setClinicSettings(mergedSettings);
    setDentists(data.dentists);
    setProcedures(data.procedures);
    triggerToast('Settings updated');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl border p-8 w-full max-w-lg space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 rounded-2xl text-white font-black text-lg">CS</div>
            <div>
              <h1 className="text-2xl font-black text-slate-900">Clinic Scheduler</h1>
              <p className="text-sm text-slate-500">Secure login</p>
            </div>
          </div>

          <div className="p-3 rounded-2xl bg-blue-50 border border-blue-100 text-xs text-slate-700 space-y-1 text-center">
            <p className="font-bold text-blue-700">Demo Notice</p>
            <p>This is a demo; some functionality may be limited or not fully available.</p>
            <p>Each role uses its own Username and PIN/password (For demo accepts any PIN for now).</p>
            <p>&nbsp;</p>
            <p>
              Software designed &amp; developed by{' '}
              <a href="https://www.code2craft.com" className="text-blue-700 underline">
                Code2Craft
              </a>
            </p>
            <p>
              Call us / WhatsApp on{' '}
              <a href="tel:+9613129588" className="text-blue-700 underline">
                +9613129588
              </a>
            </p>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-bold text-slate-600">Role</label>
            <div className="grid grid-cols-4 gap-2">
              {(['admin', 'secretary', 'doctor', 'patient'] as const).map(role => (
                <button
                  key={role}
                  onClick={() => setLoginRole(role)}
                  className={`px-3 py-2 rounded-xl border text-sm font-bold ${
                    loginRole === role ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-slate-200 text-slate-600'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          {loginRole === 'doctor' && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-600">Doctor</label>
              <select
                className="w-full border rounded-xl p-3"
                value={loginDoctorId}
                onChange={(e) => setLoginDoctorId(e.target.value)}
              >
                {dentists.map(doc => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loginRole === 'patient' && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-600">Patient</label>
              <select
                className="w-full border rounded-xl p-3"
                value={loginPatientId}
                onChange={(e) => setLoginPatientId(e.target.value)}
              >
                {patients.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-600">PIN / Password</label>
            <input
              type="password"
              value={loginPin}
              onChange={(e) => setLoginPin(e.target.value)}
              className="w-full border rounded-xl p-3"
              placeholder="Enter role PIN (demo accepts any)"
            />
          </div>

          <button
            onClick={handleLogin}
            className="w-full py-3 rounded-xl bg-blue-600 text-white font-black hover:bg-blue-700 disabled:opacity-50"
            disabled={(loginRole === 'doctor' && !loginDoctorId) || (loginRole === 'patient' && !loginPatientId)}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-slate-50 font-sans text-slate-900">
        <Sidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          clinicName={clinicSettings.clinicName}
          logoUrl={clinicSettings.logoUrl}
          onLogout={handleLogout}
          currentUserRole={currentUser.role}
        />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Header
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddPatient={() => {
            setEditingPatient(null);
            setPatientForm(getEmptyPatient());
            setIsPatientModalOpen(true);
          }}
          onBookAppointment={() => {
            setEditingAppt(null);
            setApptForm(getEmptyAppt());
            setIsApptModalOpen(true);
          }}
          showAddPatient={currentUser?.role !== 'patient'}
        />

        <Toast toast={showToast} />

        <div className="flex-1 overflow-y-auto p-10">
          {activeTab === 'dashboard' && (
            <Dashboard
              patients={patients}
              appointments={visibleAppointments}
              userProfile={currentUserProfile}
            />
          )}
          {activeTab === 'patients' && (
            <PatientRegistry
              patients={filteredPatients}
              appointments={visibleAppointments}
              selectedPatientId={selectedPatientId}
              onSelectPatient={setSelectedPatientId}
              onEditPatient={handleEditPatient}
            />
          )}
          {activeTab === 'appointments' && (
            <AppointmentsView
              appointments={visibleAppointments}
              patients={patients}
              dentists={dentists}
              onEditAppointment={handleEditAppointment}
              onViewAppointment={handleEditAppointment}
              onDeleteAppointment={currentUser.role === 'patient' ? undefined : handleDeleteAppointment}
              isPatient={currentUser.role === 'patient'}
            />
          )}
          {activeTab === 'followup' && (
            <FollowUpView
              patients={visiblePatients}
              appointments={visibleAppointments}
              dentists={dentists}
              onOpenPatient={
                currentUser.role === 'patient'
                  ? undefined
                  : (id) => {
                      setSearchQuery('');
                      setSelectedPatientId(id);
                      setActiveTab('patients');
                    }
              }
              onSendReminder={
                currentUser.role === 'patient'
                  ? undefined
                  : (id) => {
                      enqueueReminder(id);
                      setActiveTab('reminders');
                    }
              }
            />
          )}
          {activeTab === 'reminders' && (
            <ReminderQueue
              reminders={currentUser.role === 'patient' ? reminders.filter(r => r.patientId === currentUser.patientId) : reminders}
              onUpdateStatus={currentUser.role === 'patient' ? undefined : updateReminderStatus}
              onDelete={currentUser.role === 'patient' ? undefined : deleteReminder}
              isPatient={currentUser.role === 'patient'}
            />
          )}
          {activeTab === 'calendar' && (
            <CalendarView
              appointments={visibleAppointments}
              patients={patients}
              dentists={dentists}
              procedures={procedures}
              initialDate={calendarFocusDate || todayDateStr}
              onEditAppointment={handleEditAppointment}
              onCreateAppointment={handleCreateAppointment}
            />
          )}
          {activeTab === 'settings' && (
            (currentUser.role === 'admin' || currentUser.role === 'secretary') ? (
              <Settings
                dentists={dentists}
                procedures={procedures}
                clinicSettings={clinicSettings}
                onSave={handleSaveSettings}
              />
            ) : (
              <div className="bg-white rounded-3xl border shadow-sm p-8 text-slate-600">
                <h2 className="text-2xl font-black mb-3">Settings</h2>
                <p className="text-sm">Settings are managed by Admin/Secretary.</p>
              </div>
            )
          )}
        </div>
      </main>

      <PatientModal
        isOpen={isPatientModalOpen}
        editingPatient={editingPatient}
        patientForm={patientForm}
        onClose={() => setIsPatientModalOpen(false)}
        onSave={handleSavePatient}
        onFormChange={setPatientForm}
        contactOnly={currentUser?.role === 'patient'}
      />

      <AppointmentModal
        isOpen={isApptModalOpen}
        editingAppt={editingAppt}
        apptForm={apptForm}
        patients={patients}
        dentists={dentists}
        procedures={procedures}
        rooms={rooms}
        appointments={visibleAppointments}
        lockedPatientId={currentUser?.role === 'patient' ? currentUser.patientId : undefined}
        allowStatusEdit={currentUser?.role !== 'patient'}
        isPatient={currentUser?.role === 'patient'}
        onClose={() => setIsApptModalOpen(false)}
        onSave={handleSaveAppt}
        onFormChange={setApptForm}
      />
    </div>
  );
};

export default App;
